import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Loader2 } from "lucide-react";
import { wsFetch, todayIST } from "./theme";

const API = process.env.REACT_APP_BACKEND_URL;

export function QuickTableBooking({ token, center, date, constants, onCreated }) {
  const blank = { date, time_slot: "", num_guests: 2, guest_name: "", phone: "", celebration_type: "None" };
  const [f, setF] = useState(blank);
  const [tables, setTables] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF((x) => ({ ...x, date })); }, [date]);
  useEffect(() => {
    setPick("");
    if (!center || !f.date || !f.time_slot) { setTables([]); return; }
    wsFetch(token, { module: "bookings", center, date: f.date, slot: f.time_slot }).then((d) => setTables(d.tables || [])).catch(() => setTables([]));
  }, [token, center, f.date, f.time_slot]);
  const suggestions = useMemo(() => tables
    .filter((t) => !t.bookings.some((b) => b.time_slot === f.time_slot) && t.live_status !== "out_of_service" && !(f.date === todayIST() && ["occupied", "cleaning"].includes(t.live_status)) && t.capacity >= Number(f.num_guests || 0))
    .sort((a, b) => a.capacity - b.capacity).slice(0, 6), [tables, f.time_slot, f.num_guests]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!center) return toast.error("Select the centre at the top first");
    if (!f.guest_name.trim() || !f.phone.trim() || !f.time_slot) return toast.error("Guest name, mobile and time are required");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/bookings/create`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, center, ...f, num_guests: Number(f.num_guests), status: "Confirmed", booking_source: "Phone Call", table_allotted: pick, guest_type: "New Entry" }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not save booking");
      toast.success(`Table booking confirmed: ${d.booking_id}`);
      setF({ ...blank, date: f.date });
      onCreated?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="bi-panel" data-testid="bi-quick-booking">
      <h2 className="bi-panel-title">Quick Table Booking</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1"><Label>Booking date</Label><Input type="date" className="h-12 text-base" value={f.date} onChange={set("date")} data-testid="qb-date" /></div>
        <div className="space-y-1"><Label>Booking time *</Label>
          <select className="h-12 w-full rounded-md border bg-white px-3 text-base" value={f.time_slot} onChange={set("time_slot")} data-testid="qb-slot">
            <option value="">Select time</option>{(constants?.time_slots || []).map((s) => <option key={s}>{s}</option>)}</select></div>
        <div className="space-y-1"><Label>Number of guests *</Label><Input type="number" min="1" className="h-12 text-base" value={f.num_guests} onChange={set("num_guests")} data-testid="qb-guests" /></div>
        <div className="space-y-1"><Label>Occasion (optional)</Label>
          <select className="h-12 w-full rounded-md border bg-white px-3 text-base" value={f.celebration_type} onChange={set("celebration_type")} data-testid="qb-occasion">
            {(constants?.celebration_types || ["None"]).map((s) => <option key={s}>{s}</option>)}</select></div>
        <div className="space-y-1"><Label>Guest name *</Label><Input className="h-12 text-base" value={f.guest_name} onChange={set("guest_name")} data-testid="qb-name" /></div>
        <div className="space-y-1"><Label>Mobile number *</Label><Input inputMode="tel" className="h-12 text-base" value={f.phone} onChange={set("phone")} data-testid="qb-phone" /></div>
      </div>
      {f.time_slot && center && (
        <div className="mt-3 space-y-2" data-testid="qb-suggestions">
          <Label>Suggested tables (free in this slot, enough seats)</Label>
          {!suggestions.length ? <p className="text-sm text-amber-800">No single free table fits {f.num_guests} guests — save without a table and allot in the full booking form.</p> : (
            <div className="flex flex-wrap gap-2">{suggestions.map((t) => (
              <button key={t.table_number} type="button" onClick={() => setPick(pick === t.table_number ? "" : t.table_number)}
                className={`rounded-xl border-2 px-4 py-2 text-base font-semibold ${pick === t.table_number ? "border-[#7a0f1d] bg-[#7a0f1d] text-[#fff3c4]" : "border-emerald-600 bg-emerald-50 text-emerald-900"}`} data-testid={`qb-table-${t.table_number}`}>
                {pick === t.table_number && <CheckCircle2 className="mr-1 inline h-4 w-4" />}{t.table_number} · {t.capacity} seats</button>))}</div>)}
        </div>)}
      <Button className="mt-4 h-12 w-full bg-[#7a0f1d] text-base hover:bg-[#5a0a14]" onClick={save} disabled={busy} data-testid="qb-confirm">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm Table Booking</Button>
    </div>
  );
}
